import { v4 as uuidv4 } from 'uuid';

import { supabase } from './supabaseClient';

const projects_table_name = 'ml_projects_staging';
const milestones_table_name = 'ml_project_milestones';
const submissions_table_name = 'ml_project_submissions';

/**
 * Fetch all projects for the current user
 * @param {Object} filters - Optional filters { status, courseId, projectType }
 * @returns {Promise<Object>} { data: projects[], error }
 */
export const fetchUserProjects = async (filters = {}) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  let query = supabase
    .from(projects_table_name)
    .select('*')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false });

  // Apply filters
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.courseId) {
    query = query.eq('course_id', filters.courseId);
  }
  if (filters.projectType) {
    query = query.eq('project_type', filters.projectType);
  }

  const { data, error } = await query;

  if (error) {
    return { error };
  }

  return { data };
};

/**
 * Fetch a single project by ID with all milestones
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data: project, error }
 */
export const fetchProjectById = async (projectId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  // Fetch project scoped to current user
  const { data: project, error: projectError } = await supabase
    .from(projects_table_name)
    .select('*')
    .eq('id', projectId)
    .eq('user_id', session.user.id)
    .single();

  if (projectError) {
    return { error: projectError };
  }

  // Fetch milestones for this project
  const { data: milestones, error: milestonesError } = await supabase
    .from(milestones_table_name)
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (milestonesError) {
    return { error: milestonesError };
  }

  return {
    data: {
      ...project,
      milestones: milestones || [],
    },
  };
};

/**
 * Create a new project with milestones
 * @param {Object} projectData - Project data
 * @returns {Promise<Object>} { data: project, error }
 */
export const createProject = async (projectData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const projectId = projectData.id || uuidv4();

  // Prepare project data
  const project = {
    id: projectId,
    user_id: session.user.id,
    course_id: projectData.course_id || null,
    unit_id: projectData.unit_id || null,
    title: projectData.title,
    description: projectData.description,
    status: projectData.status || 'not_started',
    progress_percentage: projectData.progress_percentage || 0,
    ai_generated: projectData.ai_generated || false,
    generation_prompt: projectData.generation_prompt || null,
    project_type: projectData.project_type || null,
    sharing_platforms: projectData.sharing_platforms || [],
    // Align with ml_projects_staging schema: use github_url column
    github_url: projectData.github_url || projectData.github_repo_url || null,
    blog_post_url: projectData.blog_post_url || null,
    video_url: projectData.video_url || null,
    portfolio_url: projectData.portfolio_url || null,
    social_media_urls: projectData.social_media_urls || {},
    is_public: projectData.is_public || false,
    learning_objectives: projectData.learning_objectives || [],
    prerequisites: projectData.prerequisites || [],
    technologies: projectData.technologies || [],
    deliverables: projectData.deliverables || [],
  };

  // Insert project
  const { data: savedProject, error: projectError } = await supabase
    .from(projects_table_name)
    .insert([project])
    .select()
    .single();

  if (projectError) {
    // eslint-disable-next-line no-console
    console.error('[createProject] Error inserting project:', projectError);
    return { error: projectError };
  }

  // Insert milestones if provided
  const milestones = projectData.milestones || [];
  let savedMilestones = [];

  if (milestones.length > 0) {
    const milestonesToInsert = milestones.map((milestone, index) => {
      const rawTasks = milestone.tasks || [];
      const normalizedTasks = rawTasks.map((task, taskIndex) => {
        if (typeof task === 'string') {
          return {
            id: uuidv4(),
            title: task,
            description: null,
            completed: false,
            order_index: taskIndex + 1,
          };
        }

        if (task && typeof task === 'object') {
          return {
            id: task.id || uuidv4(),
            title: task.title || task.name || '',
            description: task.description || null,
            completed: Boolean(task.completed),
            order_index: task.order_index ?? taskIndex + 1,
          };
        }

        return {
          id: uuidv4(),
          title: '',
          description: null,
          completed: false,
          order_index: taskIndex + 1,
        };
      });

      return {
        id: milestone.id || uuidv4(),
        project_id: projectId,
        title: milestone.title,
        description: milestone.description,
        order_index: milestone.order_index ?? index + 1,
        // Normalize status to allowed values per ml_project_milestones_status_check
        status:
          milestone.status === 'in_progress' ||
          milestone.status === 'completed' ||
          milestone.status === 'blocked'
            ? milestone.status
            : 'pending',
        estimated_hours: milestone.estimated_hours || 3,
        actual_hours: milestone.actual_hours || null,
        due_date: milestone.due_date || null,
        tasks: normalizedTasks,
        resources: milestone.resources || [],
        success_criteria: milestone.success_criteria || [],
        notes: milestone.notes || null,
        ai_hints: milestone.ai_hints || null,
      };
    });

    const { data: insertedMilestones, error: milestonesError } = await supabase
      .from(milestones_table_name)
      .insert(milestonesToInsert)
      .select();

    if (milestonesError) {
      // eslint-disable-next-line no-console
      console.error('[createProject] Error inserting milestones:', milestonesError);
      return { error: milestonesError };
    }

    savedMilestones = insertedMilestones || [];
  }

  // eslint-disable-next-line no-console
  console.log('[createProject] Project created successfully:', savedProject.id);

  return {
    data: {
      ...savedProject,
      milestones: savedMilestones,
    },
  };
};

/**
 * Update an existing project
 * @param {string} projectId - Project ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} { data: project, error }
 */
export const updateProject = async (projectId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  // Remove milestones from updates if present (handle separately)
  // eslint-disable-next-line no-unused-vars
  const { milestones, ...projectUpdates } = updates;

  const { data, error } = await supabase
    .from(projects_table_name)
    .update(projectUpdates)
    .eq('id', projectId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateProject] Error updating project:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[updateProject] Project updated successfully:', projectId);

  return { data };
};

/**
 * Delete a project (soft delete by setting status to 'archived')
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data, error }
 */
export const deleteProject = async (projectId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(projects_table_name)
    .update({ status: 'archived' })
    .eq('id', projectId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[deleteProject] Error archiving project:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[deleteProject] Project archived successfully:', projectId);

  return { data };
};

/**
 * Hard delete a project (permanently remove from database)
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data, error }
 */
export const hardDeleteProject = async (projectId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(projects_table_name)
    .delete()
    .eq('id', projectId)
    .eq('user_id', session.user.id);

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[hardDeleteProject] Error deleting project:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[hardDeleteProject] Project deleted permanently:', projectId);

  return { data };
};

/**
 * Update project progress percentage
 * @param {string} projectId - Project ID
 * @param {number} progress - Progress percentage (0-100)
 * @returns {Promise<Object>} { data, error }
 */
export const updateProjectProgress = async (projectId, progress) => {
  return updateProject(projectId, {
    progress_percentage: Math.min(100, Math.max(0, progress)),
  });
};

/**
 * Mark project as completed
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data, error }
 */
export const completeProject = async (projectId) => {
  return updateProject(projectId, {
    status: 'completed',
    progress_percentage: 100,
    completed_at: new Date().toISOString(),
  });
};

/**
 * Fetch milestones for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data: milestones[], error }
 */
export const fetchProjectMilestones = async (projectId) => {
  const { data, error } = await supabase
    .from(milestones_table_name)
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (error) {
    return { error };
  }

  return { data };
};

/**
 * Create a new milestone for a project
 * @param {Object} milestoneData - Milestone data
 * @returns {Promise<Object>} { data: milestone, error }
 */
export const createMilestone = async (milestoneData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const milestone = {
    id: milestoneData.id || uuidv4(),
    project_id: milestoneData.project_id,
    title: milestoneData.title,
    description: milestoneData.description,
    order_index: milestoneData.order_index,
    status: milestoneData.status || 'pending',
    estimated_hours: milestoneData.estimated_hours || 3,
    actual_hours: milestoneData.actual_hours || null,
    due_date: milestoneData.due_date || null,
    tasks: milestoneData.tasks || [],
    resources: milestoneData.resources || [],
    success_criteria: milestoneData.success_criteria || [],
    notes: milestoneData.notes || null,
    ai_hints: milestoneData.ai_hints || null,
  };

  const { data, error } = await supabase
    .from(milestones_table_name)
    .insert([milestone])
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[createMilestone] Error creating milestone:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[createMilestone] Milestone created successfully:', data.id);

  return { data };
};

/**
 * Update a milestone
 * @param {string} milestoneId - Milestone ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} { data: milestone, error }
 */
export const updateMilestone = async (milestoneId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(milestones_table_name)
    .update(updates)
    .eq('id', milestoneId)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateMilestone] Error updating milestone:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[updateMilestone] Milestone updated successfully:', milestoneId);

  return { data };
};

/**
 * Delete a milestone
 * @param {string} milestoneId - Milestone ID
 * @returns {Promise<Object>} { data, error }
 */
export const deleteMilestone = async (milestoneId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase.from(milestones_table_name).delete().eq('id', milestoneId);

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[deleteMilestone] Error deleting milestone:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[deleteMilestone] Milestone deleted successfully:', milestoneId);

  return { data };
};

/**
 * Mark a milestone as completed
 * @param {string} milestoneId - Milestone ID
 * @returns {Promise<Object>} { data, error }
 */
export const completeMilestone = async (milestoneId) => {
  return updateMilestone(milestoneId, {
    status: 'completed',
    completed_at: new Date().toISOString(),
  });
};

/**
 * Update milestone task completion status
 * @param {string} milestoneId - Milestone ID
 * @param {string} taskId - Task ID
 * @param {boolean} completed - Completion status
 * @returns {Promise<Object>} { data, error }
 */
export const updateMilestoneTask = async (milestoneId, taskId, completed) => {
  // First, fetch the milestone to get current tasks
  const { data: milestone, error: fetchError } = await supabase
    .from(milestones_table_name)
    .select('tasks')
    .eq('id', milestoneId)
    .single();

  if (fetchError) {
    return { error: fetchError };
  }

  // Update the specific task
  const updatedTasks = (milestone.tasks || []).map((task) =>
    task.id === taskId ? { ...task, completed } : task
  );

  // Update the milestone with new tasks
  return updateMilestone(milestoneId, { tasks: updatedTasks });
};

/**
 * Submit a project or milestone
 * @param {Object} submissionData - Submission data
 * @returns {Promise<Object>} { data: submission, error }
 */
export const submitProject = async (submissionData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const submission = {
    id: submissionData.id || uuidv4(),
    project_id: submissionData.project_id,
    milestone_id: submissionData.milestone_id || null,
    user_id: session.user.id,
    submission_type: submissionData.submission_type || 'final',
    submission_url: submissionData.submission_url,
    description: submissionData.description || null,
    feedback: submissionData.feedback || null,
    ai_feedback: submissionData.ai_feedback || null,
    score: submissionData.score || null,
  };

  const { data, error } = await supabase
    .from(submissions_table_name)
    .insert([submission])
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[submitProject] Error creating submission:', error);
    return { error };
  }

  // eslint-disable-next-line no-console
  console.log('[submitProject] Submission created successfully:', data.id);

  return { data };
};

/**
 * Fetch submissions for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data: submissions[], error }
 */
export const fetchSubmissions = async (projectId) => {
  const { data, error } = await supabase
    .from(submissions_table_name)
    .select('*')
    .eq('project_id', projectId)
    .order('submitted_at', { ascending: false });

  if (error) {
    return { error };
  }

  return { data };
};

/**
 * Update a submission
 * @param {string} submissionId - Submission ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} { data: submission, error }
 */
export const updateSubmission = async (submissionId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(submissions_table_name)
    .update(updates)
    .eq('id', submissionId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[updateSubmission] Error updating submission:', error);
    return { error };
  }

  return { data };
};

/**
 * Delete a submission
 * @param {string} submissionId - Submission ID
 * @returns {Promise<Object>} { data, error }
 */
export const deleteSubmission = async (submissionId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(submissions_table_name)
    .delete()
    .eq('id', submissionId)
    .eq('user_id', session.user.id);

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[deleteSubmission] Error deleting submission:', error);
    return { error };
  }

  return { data };
};

/**
 * Get project statistics for the current user
 * @returns {Promise<Object>} { data: stats, error }
 */
export const getUserProjectStats = async () => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase.rpc('get_user_project_stats', {
    p_user_id: session.user.id,
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[getUserProjectStats] Error fetching stats:', error);
    return { error };
  }

  return { data: data[0] || {} };
};

/**
 * Fork projects from an original course to a new forked course
 * This is called when a course is forked to copy all projects
 * @param {string} originalCourseId - Original course ID
 * @param {string} newCourseId - New forked course ID
 * @param {string} newOwnerId - New owner's user ID
 * @returns {Promise<Object>} { data: forkedProjects[], error }
 */
export const forkProjectsForCourse = async (originalCourseId, newCourseId, newOwnerId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  // eslint-disable-next-line no-console
  console.log('[forkProjectsForCourse] Forking projects from course:', originalCourseId);

  // Fetch all projects from the original course
  const { data: originalProjects, error: fetchError } = await supabase
    .from(projects_table_name)
    .select('*')
    .eq('course_id', originalCourseId);

  if (fetchError) {
    // eslint-disable-next-line no-console
    console.error('[forkProjectsForCourse] Error fetching original projects:', fetchError);
    return { error: fetchError };
  }

  if (!originalProjects || originalProjects.length === 0) {
    // eslint-disable-next-line no-console
    console.log('[forkProjectsForCourse] No projects to fork');
    return { data: [] };
  }

  // eslint-disable-next-line no-console
  console.log('[forkProjectsForCourse] Found projects to fork:', originalProjects.length);

  // Prepare forked projects
  const forkedProjects = originalProjects.map((project) => ({
    id: uuidv4(), // New ID for forked project
    user_id: newOwnerId,
    course_id: newCourseId,
    unit_id: project.unit_id, // Keep same unit reference (units are also forked)
    title: project.title,
    description: project.description,
    status: 'not_started', // Reset status for forked project
    progress_percentage: 0, // Reset progress
    ai_generated: project.ai_generated,
    generation_prompt: project.generation_prompt,
    project_type: project.project_type,
    sharing_platforms: project.sharing_platforms,
    github_url: null, // Don't copy sharing URLs
    blog_post_url: null,
    video_url: null,
    portfolio_url: null,
    social_media_urls: {},
    is_public: false, // Reset to private
    learning_objectives: project.learning_objectives,
    prerequisites: project.prerequisites,
    technologies: project.technologies,
    deliverables: project.deliverables,
  }));

  // Insert forked projects
  const { data: insertedProjects, error: insertError } = await supabase
    .from(projects_table_name)
    .insert(forkedProjects)
    .select();

  if (insertError) {
    // eslint-disable-next-line no-console
    console.error('[forkProjectsForCourse] Error inserting forked projects:', insertError);
    return { error: insertError };
  }

  // eslint-disable-next-line no-console
  console.log('[forkProjectsForCourse] Successfully forked projects:', insertedProjects.length);

  // Now fork milestones for each project
  for (let i = 0; i < originalProjects.length; i++) {
    const originalProjectId = originalProjects[i].id;
    const newProjectId = insertedProjects[i].id;

    // Fetch milestones for original project
    const { data: originalMilestones, error: milestonesError } = await supabase
      .from(milestones_table_name)
      .select('*')
      .eq('project_id', originalProjectId)
      .order('order_index', { ascending: true });

    if (milestonesError) {
      // eslint-disable-next-line no-console
      console.error('[forkProjectsForCourse] Error fetching milestones:', milestonesError);
      continue; // Skip this project's milestones but continue with others
    }

    if (originalMilestones && originalMilestones.length > 0) {
      // Prepare forked milestones
      const forkedMilestones = originalMilestones.map((milestone) => ({
        id: uuidv4(),
        project_id: newProjectId,
        title: milestone.title,
        description: milestone.description,
        order_index: milestone.order_index,
        status: 'pending', // Reset status
        estimated_hours: milestone.estimated_hours,
        actual_hours: null, // Reset actual hours
        due_date: null, // Don't copy due dates
        tasks: milestone.tasks,
        resources: milestone.resources,
        success_criteria: milestone.success_criteria,
        notes: null, // Don't copy notes
        ai_hints: milestone.ai_hints,
      }));

      // Insert forked milestones
      const { error: insertMilestonesError } = await supabase
        .from(milestones_table_name)
        .insert(forkedMilestones);

      if (insertMilestonesError) {
        // eslint-disable-next-line no-console
        console.error('[forkProjectsForCourse] Error inserting milestones:', insertMilestonesError);
      }
    }
  }

  return { data: insertedProjects };
};

/**
 * Fetch all projects for a specific course
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} { data: projects[], error }
 */
export const fetchProjectsByCourseId = async (courseId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(projects_table_name)
    .select('*')
    .eq('course_id', courseId)
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false });

  if (error) {
    return { error };
  }

  return { data };
};

/**
 * Fetch all projects for a specific unit
 * @param {string} unitId - Unit ID
 * @returns {Promise<Object>} { data: projects[], error }
 */
export const fetchProjectsByUnitId = async (unitId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const { data, error } = await supabase
    .from(projects_table_name)
    .select('*')
    .eq('unit_id', unitId)
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false });

  if (error) {
    return { error };
  }

  return { data };
};

/**
 * Start project timer
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data, error }
 */
export const startProjectTimer = async (projectId) => {
  const now = new Date().toISOString();
  return updateProject(projectId, {
    status: 'in_progress',
    started_at: now,
    last_worked_at: now,
    timer_active: true,
    timer_paused: false,
  });
};

/**
 * Pause project timer
 * @param {string} projectId - Project ID
 * @param {number} elapsedSeconds - Current elapsed seconds
 * @returns {Promise<Object>} { data, error }
 */
export const pauseProjectTimer = async (projectId, elapsedSeconds) => {
  return updateProject(projectId, {
    timer_paused: true,
    elapsed_seconds: elapsedSeconds,
    last_worked_at: new Date().toISOString(),
  });
};

/**
 * Resume project timer
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { data, error }
 */
export const resumeProjectTimer = async (projectId) => {
  return updateProject(projectId, {
    timer_paused: false,
    last_worked_at: new Date().toISOString(),
  });
};

/**
 * Stop project timer
 * @param {string} projectId - Project ID
 * @param {number} elapsedSeconds - Total elapsed seconds
 * @returns {Promise<Object>} { data, error }
 */
export const stopProjectTimer = async (projectId, elapsedSeconds) => {
  return updateProject(projectId, {
    timer_active: false,
    timer_paused: false,
    elapsed_seconds: elapsedSeconds,
    last_worked_at: new Date().toISOString(),
  });
};
